import type { Sheet } from "@fortune-sheet/core";
import { Workbook } from "@fortune-sheet/react";
import "@fortune-sheet/react/dist/index.css";
import GridOn from "@mui/icons-material/GridOn";
import { Box, CircularProgress, type SxProps, type Theme } from "@mui/material";
import { type ClipboardEvent, forwardRef, type KeyboardEvent, type ReactNode, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NEUTRAL } from "../../../theme/tokens";
import { extractStyleOverlay, type SheetStyleOverlay, styleOverlaysEqual } from "../../../utils/fortuneSheetStyleOverlay";
import { ErrorBoundary } from "../../ErrorBoundary";
import { EmptyState } from "../molecules/EmptyState";

export type SpreadsheetPreviewHandle = {
	/**
	 * Swallow the next `onColumnWidthsChange`. Arm this immediately before a *programmatic* width
	 * change (a reset): the widget re-enters its change callback with the new widths, and that echo
	 * would otherwise be reported back as a user edit and loop.
	 */
	suppressNextColumnWidthsChange: () => void;
};

/**
 * Opt-in session formatting for one preview. ONE prop rather than six, so the "formatting off" branch is
 * a single `undefined` check and no surface can half-enable it — `allowEdit` without the content guard
 * would make this widget editable.
 */
export type SpreadsheetPreviewFormatting = {
	/** Fires only when the user's formatting actually differs from `styleOverlay`. */
	onStyleOverlayChange: (overlay: SheetStyleOverlay) => void;
	/** The overlay currently in force, for no-op suppression. */
	styleOverlay: SheetStyleOverlay;
	/** The pristine sheets to diff against — normally the same array passed as `sheets`. */
	originalSheets: Sheet[];
};

/** The separator entry in `FORMATTING_TOOLBAR_ITEMS`, which renders as a divider with no control. */
const TOOLBAR_DIVIDER = "|";

/**
 * The formatting controls this preview offers, and deliberately no others. Every key here writes one of
 * `OVERLAY_STYLE_KEYS`, which is what makes "what you see is what both downloads get" true — the two
 * lists are each other's contract, so adding a button means adding a key.
 *
 * Excluded on purpose: `format-painter` (copies value formats, not only paint), `merge-cell`, `border`,
 * `text-wrap`, `text-rotation` and `freeze` (structure and geometry, which would move page boundaries),
 * every `*-format` numeric item (those rewrite `m`, i.e. the cell's CONTENT), and everything from
 * `conditionFormat` onwards. `undo`/`redo` are absent because their history is workbook-wide and their
 * interaction with the Delete guard below is unverified; `clear-format` is the recovery path.
 *
 * `font-size` is the one control here that changes a cell's GEOMETRY rather than its paint, and it is
 * only safe to offer because the PDF path rebuilds its workbook instead of painting the preview's
 * sheets — see `sheetsForPdf` in `DailyPrintoutButton`. A surface that adopts this bundle without that
 * rebuild would ship a control whose effect reaches the xlsx and not the PDF.
 */
const FORMATTING_TOOLBAR_ITEMS = [
	"font-size",
	"|",
	"bold",
	"italic",
	"underline",
	"strike-through",
	TOOLBAR_DIVIDER,
	"font-color",
	"background",
	TOOLBAR_DIVIDER,
	"horizontal-align",
	"vertical-align",
	TOOLBAR_DIVIDER,
	"clear-format",
];

/** Empty, not default: Fortune Sheet's custom cell menu can recurse while correcting overflow near a
 *  viewport edge. Keyboard copy remains available; the header and sheet-tab menus are entirely
 *  insert/delete/hide operations that do not belong in this fixed-workbook preview. */
const NO_CONTEXT_MENU: string[] = [];

/** Fortune Sheet's own Delete/Backspace handler writes `data[r][c] = {}` directly (`deleteSelectedCellText`),
 *  gated only on `allowEdit` — which formatting REQUIRES to be true, since `updateFormat` early-returns
 *  without it. `hooks.beforeUpdateCell` does not cover the path (it is called only from `updateCell`), so
 *  the keys have to be stopped before the widget sees them. */
const CONTENT_CLEARING_KEYS = new Set(["Delete", "Backspace"]);

/**
 * Give every toolbar control a test id, which Fortune Sheet renders itself and offers no prop for.
 *
 * Named from OUR `FORMATTING_TOOLBAR_ITEMS` by position, which is both the name a reader would look for
 * and the only stable one available. The two obvious alternatives are worse: the tooltip is localised by
 * the widget, and the icon id of the alignment combos is whichever alignment is *currently selected*, so
 * an icon-derived id silently renames itself from `-align-left` to `-align-right` the moment it is used.
 *
 * Bails out rather than guessing if the DOM and the list disagree — the toolbar collapses into a "more"
 * menu when it is too narrow for every item, and a mis-tagged control is worse than an untagged one.
 */
const stampToolbarTestIds = (root: HTMLElement, dataTestId: string): void => {
	const rendered = [...root.querySelectorAll(".fortune-toolbar > *")];
	if (rendered.length !== FORMATTING_TOOLBAR_ITEMS.length) return;

	rendered.forEach((item, index) => {
		const name = FORMATTING_TOOLBAR_ITEMS[index];
		if (name === TOOLBAR_DIVIDER) return;
		item.setAttribute("data-testid", `${dataTestId}-format-${name}`);
	});
};

/**
 * Everything `formatting` turns on, as one frozen object spread onto the widget — so a preview WITHOUT
 * formatting is handed no such prop at all. That matters: the widget merges props with
 * `_.assign(cloneDeep(defaultSettings), props)`, and lodash `assign` copies an explicit `undefined` over
 * the default, which would leave the five read-only surfaces with no toolbar list and no context menus.
 *
 * `beforeUpdateCell` blocks the commit of a value the user TYPED into a cell, and nothing else — it is
 * called from `updateCell` alone. Delete/Backspace and paste/cut each bypass it entirely and are stopped
 * by the two capture-phase guards on the wrapper instead; see those for why a hook cannot do either job.
 */
const FORMATTING_WORKBOOK_SETTINGS = {
	toolbarItems: FORMATTING_TOOLBAR_ITEMS,
	cellContextMenu: NO_CONTEXT_MENU,
	headerContextMenu: NO_CONTEXT_MENU,
	sheetTabContextMenu: NO_CONTEXT_MENU,
	hooks: { beforeUpdateCell: () => false },
};

export type SpreadsheetPreviewProps = {
	/**
	 * The workbook to show — the produced file round-tripped, so what the user approves is what
	 * downloads. Must be referentially STABLE across renders that don't change the data (component
	 * state or a query cache is fine; never build it inline): a new non-empty identity remounts the
	 * widget, because Fortune Sheet seeds its internal state from `data` once and never re-reads it.
	 */
	sheets: Sheet[];
	isLoading?: boolean;
	/** Extra force-remount signal, for a reset driven by something outside `sheets`. */
	remountKey?: number | string;
	/** Defaults to `sheets.length > 1`. Pass `false` to always hide the tab strip. */
	showSheetTabs?: boolean;
	/**
	 * The widths currently in force, keyed by 0-based column index. Used to suppress no-op reports:
	 * the widget fires on nearly every internal change, most of which are not width changes.
	 */
	columnWidths?: Record<string, number>;
	/** Fires only when a drag produced different (rounded) widths than `columnWidths`. */
	onColumnWidthsChange?: (widths: Record<string, number>) => void;
	/**
	 * Absent → the read-only preview every other surface gets, unchanged. Present → the formatting
	 * toolbar, with cell CONTENT still locked.
	 */
	formatting?: SpreadsheetPreviewFormatting;
	/** Already-translated. Defaults to `t("download.preview.empty")`. */
	emptyLabel?: ReactNode;
	emptyHint?: ReactNode;
	/** Already-translated. Defaults to `t("download.preview.error")`. */
	errorLabel?: ReactNode;
	/** Action slot under the empty/error message (e.g. a retry button). */
	action?: ReactNode;
	/** Accessible name for the preview region. Defaults to `t("preview")`. */
	ariaLabel?: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

const CENTERED: SxProps<Theme> = { display: "flex", alignItems: "center", justifyContent: "center", height: "100%" };

/**
 * The column widths across every sheet in the workbook, preferring any that differ from what the
 * caller already holds.
 *
 * Fortune Sheet hands `onChange` the WHOLE workbook, not the sheets that changed, so reading
 * `changed[0]` only ever saw the first sheet — a drag on any other tab was silently dropped. Every
 * sheet is built from the same widths, so the one that differs from `current` is the one just dragged;
 * merging naively would let an untouched sheet's stale value overwrite it.
 *
 * Returns null when no sheet reports widths at all, which is not the same as "no width changed".
 */
const draggedColumnWidths = (sheets: Sheet[], current: Record<string, number>): Record<string, number> | null => {
	let sawAny = false;
	const merged: Record<string, number> = {};

	for (const sheet of sheets) {
		const lengths = sheet.config?.columnlen;
		if (!lengths) continue;
		sawAny = true;
		for (const [key, value] of Object.entries(lengths)) {
			const rounded = Math.round(value);
			if (merged[key] === undefined || rounded !== current[key]) merged[key] = rounded;
		}
	}

	return sawAny ? merged : null;
};

/**
 * Read-only spreadsheet preview panel: the Fortune Sheet widget plus the chrome every export dialog
 * was hand-rolling. Owns the widget's CSS import (one place) and the remount it needs.
 *
 * Deliberately sizes to its parent — callers pass `flex: 1` / a height via `sx`. A default height
 * here would reflow the tall modals this lives inside.
 */
export const SpreadsheetPreview = forwardRef<SpreadsheetPreviewHandle, SpreadsheetPreviewProps>(function SpreadsheetPreview(
	{
		sheets,
		isLoading,
		remountKey,
		showSheetTabs,
		columnWidths,
		onColumnWidthsChange,
		formatting,
		emptyLabel,
		emptyHint,
		errorLabel,
		action,
		ariaLabel,
		dataTestId,
		sx,
	},
	ref,
) {
	const { t } = useTranslation();
	const suppressNextChange = useRef(false);

	useImperativeHandle(ref, () => ({
		suppressNextColumnWidthsChange: () => {
			suppressNextChange.current = true;
		},
	}));

	// The widget seeds its state from `data` once, so a rebuilt workbook needs a remount to appear.
	// Tracking the identity here rather than asking callers for a key also fixes the reverse bug: a
	// cached result arriving with no loading pass in between used to leave the previous sheet up.
	//
	// Held in state, not a ref: setting state during render is React's sanctioned way to derive from
	// props, and it is discard-safe. Bumping a ref in the render body would also count renders React
	// throws away, remounting the widget and losing the user's scroll position and selected tab.
	const [trackedSheets, setTrackedSheets] = useState<{ sheets: Sheet[] | null; remounts: number }>({ sheets: null, remounts: 0 });
	const needsRemount = sheets.length > 0 && trackedSheets.sheets !== sheets;
	if (needsRemount) setTrackedSheets({ sheets, remounts: trackedSheets.remounts + 1 });
	const autoRemountCount = needsRemount ? trackedSheets.remounts + 1 : trackedSheets.remounts;

	// Fortune Sheet lists `onChange` in its own effect's deps, so a NEW callback identity re-invokes it
	// with unchanged workbook data — and each spurious invocation is one that could consume the
	// suppression flag meant for a real programmatic change. Reading the widths from a ref keeps this
	// callback identity-stable for the widget's lifetime.
	const columnWidthsRef = useRef(columnWidths);
	columnWidthsRef.current = columnWidths;
	// Same reason, and it must be a ref for a second one: `formatting` is a fresh object every render, so
	// depending on it directly would re-register the widget's change effect on every keystroke elsewhere
	// in the dialog.
	const formattingRef = useRef(formatting);
	formattingRef.current = formatting;
	const onColumnWidthsChangeRef = useRef(onColumnWidthsChange);
	onColumnWidthsChangeRef.current = onColumnWidthsChange;

	/** The widths half of `onChange`, which fires for nearly every internal change and not only drags. */
	const reportColumnWidths = useCallback((changed: Sheet[]) => {
		const report = onColumnWidthsChangeRef.current;
		if (!report) return;
		// Consumed HERE rather than at the top of `handleChange`: the flag is armed for a programmatic
		// width reset, and letting it swallow the whole callback would drop a formatting change with it.
		if (suppressNextChange.current) {
			suppressNextChange.current = false;
			return;
		}
		const current = columnWidthsRef.current ?? {};
		const rounded = draggedColumnWidths(changed, current);
		if (!rounded) return;
		const unchanged = Object.keys(rounded).length === Object.keys(current).length && Object.entries(rounded).every(([key, value]) => current[key] === value);
		if (unchanged) return;
		report(rounded);
	}, []);

	/** The formatting half. Independent of the widths half — a drag reports no formatting and a format
	 *  click reports no widths — so neither can suppress the other. */
	const reportStyleOverlay = useCallback((changed: Sheet[]) => {
		const active = formattingRef.current;
		if (!active) return;
		const overlay = extractStyleOverlay(active.originalSheets, changed);
		if (styleOverlaysEqual(overlay, active.styleOverlay)) return;
		active.onStyleOverlayChange(overlay);
	}, []);

	// Identity-stable for the widget's lifetime, for the reason above the refs.
	const handleChange = useCallback(
		(changed: Sheet[]) => {
			reportColumnWidths(changed);
			reportStyleOverlay(changed);
		},
		[reportColumnWidths, reportStyleOverlay],
	);

	const handleKeyDownCapture = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (!formatting || !CONTENT_CLEARING_KEYS.has(event.key)) return;
			// Capture phase on OUR wrapper, so it runs before Fortune Sheet's own React `onKeyDown` on its
			// container — see `CONTENT_CLEARING_KEYS` for why a hook cannot do this.
			event.preventDefault();
			event.stopPropagation();
		},
		[formatting],
	);

	/**
	 * Blocks paste and cut, which would write cell VALUES — and grow the sheet.
	 *
	 * This has to stop the EVENT, not the operation. Fortune Sheet listens for paste with a
	 * `document.addEventListener("paste", …)` registered outside React, and its React handler calls
	 * `insertRowCol` to grow the sheet for a taller pasted table **before** it calls `core.handlePaste` —
	 * so `hooks.beforePaste` is far too late: it can refuse the content while the new rows have already
	 * been added. `core.handlePaste`'s only other guard is `isAllowEdit`, which formatting turns on.
	 *
	 * A capture-phase handler on this wrapper runs while the native event is still descending, before it
	 * can bubble up to that document listener, so `stopPropagation()` here keeps it from ever firing.
	 */
	const handleClipboardWriteCapture = useCallback(
		(event: ClipboardEvent<HTMLDivElement>) => {
			if (!formatting) return;
			event.preventDefault();
			event.stopPropagation();
		},
		[formatting],
	);

	const containerRef = useRef<HTMLDivElement>(null);

	/**
	 * Watches for the widget's toolbar and tags it.
	 *
	 * An effect alone is not enough and this was verified in the browser, not assumed: the toolbar is
	 * rendered by a LATER, Fortune-Sheet-internal render (it sizes itself against the sheet first), which
	 * never re-renders this component — so a one-shot effect always runs too early and tags nothing. An
	 * observer is the honest tool for "another library's DOM arrives when it arrives".
	 */
	useEffect(() => {
		const root = containerRef.current;
		if (!formatting || !dataTestId || !root) return;
		stampToolbarTestIds(root, dataTestId);
		const observer = new MutationObserver(() => stampToolbarTestIds(root, dataTestId));
		observer.observe(root, { childList: true, subtree: true });
		return () => observer.disconnect();
	}, [formatting, dataTestId]);

	const renderContent = () => {
		if (isLoading) {
			return (
				<Box sx={CENTERED} role="status" data-testid={dataTestId ? `${dataTestId}-loading` : undefined}>
					<CircularProgress />
				</Box>
			);
		}
		if (sheets.length === 0) {
			return (
				<EmptyState
					dataTestId={dataTestId ? `${dataTestId}-empty` : undefined}
					icon={<GridOn />}
					title={emptyLabel ?? t("download.preview.empty")}
					hint={emptyHint}
					action={action}
				/>
			);
		}
		return (
			<Workbook
				key={`${remountKey ?? ""}-${autoRemountCount}`}
				data={sheets}
				onChange={handleChange}
				// REQUIRED by formatting, not a relaxation of it: `updateFormat` early-returns on
				// `isAllowEdit`, so every toolbar button is inert without this. Content stays locked by
				// `beforeUpdateCell`, the key guard and the emptied context menus.
				allowEdit={formatting !== undefined}
				showToolbar={formatting !== undefined}
				showFormulaBar={false}
				showSheetTabs={showSheetTabs ?? sheets.length > 1}
				{...(formatting ? FORMATTING_WORKBOOK_SETTINGS : undefined)}
			/>
		);
	};

	return (
		<Box
			data-testid={dataTestId}
			role="region"
			aria-label={ariaLabel ?? t("preview")}
			aria-busy={isLoading}
			ref={containerRef}
			onKeyDownCapture={handleKeyDownCapture}
			onPasteCapture={handleClipboardWriteCapture}
			onCutCapture={handleClipboardWriteCapture}
			sx={[
				{
					border: `1px solid ${NEUTRAL[300]}`,
					borderRadius: 1,
					overflow: "hidden",
					position: "relative",
					minWidth: 0,
					minHeight: 0,
					// Fortune Sheet's "Add [n] more rows at bottom" strip, hidden because this preview
					// formats a FIXED workbook and growing it is not an offer we make — it is the last
					// control left implying otherwise, now that paste and the context menus are shut.
					//
					// CSS is the only lever: the widget gates the strip on `allowEdit` ALONE, through a
					// hardcoded inline `display`, and formatting requires `allowEdit` (see the prop below).
					// `!important` is therefore load-bearing — an inline style beats any class.
					//
					// `display: none` rather than `visibility`/opacity so the strip leaves the tab order
					// too: it carries `tabIndex={0}` on itself and on both of its buttons.
					//
					// Matched by CLASS: the widget emits a hardcoded `id` that is not per-sheet-unique.
					// Takes the strip's "Back to the top" button with it, which this preview is too short
					// to need. Nothing reflows — the strip is `position: absolute`, an overlay.
					"& .luckysheet-bottom-controll-row": { display: "none !important" },
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<ErrorBoundary
				fallback={
					<EmptyState
						dataTestId={dataTestId ? `${dataTestId}-error` : undefined}
						icon={<GridOn />}
						title={errorLabel ?? t("download.preview.error")}
						action={action}
					/>
				}
			>
				{renderContent()}
			</ErrorBoundary>
		</Box>
	);
});
